import * as React from 'react';
import { Box } from '@locus-taxy/disco-ui';
import { loadRemote } from '@module-federation/runtime';
import { ensureRemoteRegistered } from './remoteRegistration';
import { getTestId } from '@locus-taxy/encore-utils';
import type { RelatedGridFilter } from '@locus-taxy/entity-detail/grid-adapter';
import LoadingSpinner from '../components/LoadingSpinner';
import { GOOGLE_MAPS_API_KEY } from '../config';
import { useRouteContext } from '../hooks/useRouteContext';
import { TestIdAction } from '../utils/testIdUtils';
import type {
  EntityDetailsBodyProps,
  EntityFrontendPresentation,
} from '../components/EntityDetails/types';
import type { CustomDetailsConfig } from './customDetailsConfig';

type CustomDetailsRemoteProps = {
  clientId?: string;
  entityName?: string;
  entityTitle?: string;
  rootInstanceKey?: string;
  entityData?: Record<string, unknown>;
  detailsForm?: unknown;
  showTimeline?: boolean;
  entitySchemaInfo?: unknown;
  frontend?: EntityFrontendPresentation;
  usecase?: string;
  /** True when mounted from a details-view `custom` section slot rather than
   *  the whole-page custom-app mechanism (DP-5128). */
  embedded?: boolean;
  /** The parent-scope filter resolved from a `custom` section's own join
   *  `path` (DP-5128 Decision 1) — undefined for the whole-page mechanism. */
  parentFilter?: RelatedGridFilter;
  /** Host-provided Google Maps API key, forwarded so map-using MFEs
   *  (e.g. the DP-4118 Planning / Control Tower apps) can initialise their
   *  own map without re-reading host env. Optional — MFEs that don't render
   *  a map ignore it. */
  googleMapsApiKey?: string;
};

type CustomDetailsRemoteModule = {
  default: React.ComponentType<CustomDetailsRemoteProps>;
};

function createLazyRemote(config: CustomDetailsConfig) {
  return React.lazy(() => {
    return loadRemote<CustomDetailsRemoteModule>(config.remoteModule).then(
      value => {
        if (!value) {
          throw new Error(
            `[CustomDetailsRemoteContainer] loadRemote(${config.remoteModule}) returned null`
          );
        }
        return { default: value.default };
      }
    );
  });
}

interface CustomAppRemoteContainerProps extends EntityDetailsBodyProps {
  config: CustomDetailsConfig;
  remountKey?: number;
  embedded?: boolean;
  parentFilter?: RelatedGridFilter;
}

export function CustomAppRemoteContainer({
  config,
  clientId,
  entityName,
  entityTitle,
  rootInstanceKey,
  entityData,
  detailsForm,
  showTimeline,
  remountKey,
  entitySchemaInfo,
  frontend,
  usecase,
  embedded,
  parentFilter,
}: Readonly<CustomAppRemoteContainerProps>) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const { entity, page } = useRouteContext();

  const hasClientId = !!clientId?.trim();

  // Register (or force-replace on tenant switch) before the lazy MFE mounts and
  // calls loadRemote. Skip while clientId is empty (e.g. during logout) — that
  // resolves the 'client' fallback origin and 404s on per-tenant edges.
  if (hasClientId) {
    ensureRemoteRegistered(config.remoteName, config.remoteEntry);
  }

  const RemoteDetailsLazy = React.useMemo(
    () => createLazyRemote(config),
    [
      config.entityName,
      config.remoteName,
      config.remoteEntry,
      config.remoteModule,
    ]
  );

  return (
    <Box
      ref={containerRef}
      className="flex h-full min-h-0 flex-col overflow-auto"
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: config.testIdObject,
        entity: entity ?? config.defaultRouteEntity,
        page: page ?? 'details',
      })}
    >
      {hasClientId ? (
        <React.Suspense fallback={<LoadingSpinner />}>
          <RemoteDetailsLazy
            key={remountKey}
            clientId={clientId}
            entityName={entityName}
            entityTitle={entityTitle}
            rootInstanceKey={rootInstanceKey}
            entityData={entityData}
            detailsForm={detailsForm}
            showTimeline={showTimeline}
            entitySchemaInfo={entitySchemaInfo}
            frontend={frontend}
            usecase={usecase}
            embedded={embedded}
            parentFilter={parentFilter}
            googleMapsApiKey={GOOGLE_MAPS_API_KEY}
          />
        </React.Suspense>
      ) : (
        <LoadingSpinner />
      )}
    </Box>
  );
}
